import { useState, useEffect } from "react";
import { useWallet } from "@solana/wallet-adapter-react";
import { useAuth } from "../../contexts/AuthContext";
import { ArrowRight, Loader2, Wallet } from "lucide-react";
import { Button } from "../ui/button";
import { WalletMultiButton } from "@solana/wallet-adapter-react-ui";
import "./AuthenticationFlow.css";

interface AuthenticationFlowProps {
  onAuthenticated?: () => void;
  className?: string;
}

export function AuthenticationFlow({
  onAuthenticated,
  className = "",
}: AuthenticationFlowProps) {
  const wallet = useWallet();
  const { isAuthenticated, signNonce, signInViaLedger } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [authMethod, setAuthMethod] = useState<"nonce" | "ledger" | null>(null);

  useEffect(() => {
    if (isAuthenticated && onAuthenticated) {
      onAuthenticated();
    }
  }, [isAuthenticated, onAuthenticated]);

  // Reset error when wallet changes
  useEffect(() => {
    setError(null);
  }, [wallet.publicKey]);

  const handleSignNonce = async () => {
    setIsLoading(true);
    setError(null);
    setAuthMethod("nonce");

    // Set a timeout to prevent indefinite loading
    const timeoutId = setTimeout(() => {
      setIsLoading(false);
      setAuthMethod(null);
      setError("Authentication timed out. Please try again.");
    }, 30000); // 30 second timeout

    try {
      const success = await signNonce();
      clearTimeout(timeoutId);
      if (!success) {
        setError("Failed to authenticate. Please try again.");
      }
    } catch (err) {
      clearTimeout(timeoutId);
      setError("An error occurred during authentication.");
      console.error("Sign nonce error:", err);
    } finally {
      setIsLoading(false);
      setAuthMethod(null);
    }
  };

  const handleSignViaLedger = async () => {
    setIsLoading(true);
    setError(null);
    setAuthMethod("ledger");

    // Set a timeout to prevent indefinite loading
    const timeoutId = setTimeout(() => {
      setIsLoading(false);
      setAuthMethod(null);
      setError("Ledger authentication timed out. Please try again.");
    }, 60000); // 60 second timeout for Ledger (longer because it involves a transaction)

    try {
      const success = await signInViaLedger();
      clearTimeout(timeoutId);
      if (!success) {
        setError("Failed to authenticate via Ledger. Please try again.");
      }
    } catch (err) {
      clearTimeout(timeoutId);
      setError("An error occurred during Ledger authentication.");
      console.error("Ledger sign error:", err);
    } finally {
      setIsLoading(false);
      setAuthMethod(null);
    }
  };

  // If wallet is not connected, show connect wallet button
  if (!wallet.connected || !wallet.publicKey) {
    return (
      <div className={className}>
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 font-body">
            {error}
          </div>
        )}
        <div className="">
          <WalletMultiButton className="group px-8 py-6 transition-all duration-300 text-base font-normal tracking-wide rounded-md font-heading bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white">
            <span className="inline-flex items-center gap-2 group-hover:translate-x-1 transition-transform duration-300">
              <Wallet className="w-4 h-4" />
              CONNECT WALLET
              <ArrowRight className="w-4 h-4 opacity-100 transition-all duration-300" />
            </span>
          </WalletMultiButton>
        </div>
      </div>
    );
  }

  // If wallet is connected but not authenticated, show sign nonce options
  if (!isAuthenticated) {
    return (
      <div className={className}>
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 font-body">
            {error}
          </div>
        )}

        <div className="space-y-3">
          <Button
            variant="degen"
            className="w-full group px-8 py-6 transition-all duration-300 text-base font-normal tracking-wide rounded-md font-heading"
            onClick={handleSignNonce}
            disabled={isLoading}
          >
            <span className="inline-flex items-center gap-2 group-hover:translate-x-1 transition-transform duration-300">
              SIGN IN WITH WALLET
              {isLoading && authMethod === "nonce" ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <ArrowRight className="w-4 h-4 opacity-100 transition-all duration-300" />
              )}
            </span>
          </Button>

          {wallet.publicKey?.toBase58() &&
            (!wallet.signMessage || wallet.wallet?.adapter?.name === "Ledger") && (
              <>
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-gray-300" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-2 text-gray-500">or</span>
                  </div>
                </div>

                <Button
                  variant="outline"
                  className="w-full group px-8 py-6 transition-all duration-300 text-base font-normal tracking-wide rounded-md font-heading border-gray-300"
                  onClick={handleSignViaLedger}
                  disabled={isLoading}
                >
                  <span className="inline-flex items-center gap-2 group-hover:translate-x-1 transition-transform duration-300">
                    SIGN IN VIA LEDGER
                    {isLoading && authMethod === "ledger" ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <ArrowRight className="w-4 h-4 opacity-100 transition-all duration-300" />
                    )}
                  </span>
                </Button>
              </>
            )}
        </div>

        <p className="text-sm text-gray-500 mt-4 font-body">
          Sign a message to authenticate your wallet ownership.
        </p>
      </div>
    );
  }

  // If authenticated, render nothing (parent component will handle the authenticated state)
  return null;
}
